import {
  Box,
  Button,
  Checkbox,
  Drawer,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  styled,
  tableCellClasses
} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import Pagination from '../../../../components/TablePagination'
import React, { useEffect, useRef } from 'react'
import { Button as GenericButton } from '../../../../components/Button'
import { type ConnectedProps, connect, useSelector } from 'react-redux'
import { type RootState } from '../../../../store'
import { visuallyHidden } from '@mui/utils'
import AuthError from '../../../../components/ErrorComponents/AuthError'
import type { HsmKeyTypeMapping } from '../../types'
import LanguageStrings from '../../../../i18n/locales'
import {
  fetchFilterTypesForKeyDeviceMappings,
  fetchKeyTypeMappingsList,
  fetchFilteredActiveDeviceKeyMappingList,
  deactivateKeyMapping
} from '../../redux/actionCreator'
import SearchBox from '../../../../components/SearchBox'
import CloseIcon from '@mui/icons-material/Close'
import ReplayIcon from '@mui/icons-material/Replay'
import FilterIcon from '../../../../assets/filterIcon.png'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import type { KeyTypeMappingRequest } from '../../services/deactivateKeyMapping'
import type { KeyMappingsListState } from '../../redux/getActiveKeyTypeMappingsListSlice'
import type { HsmKeyTypeMappingListState } from '../../redux/getFilteredActiveDeviceKeyMappingList'
import { hsmKeyTypeMappingListLoadingAction } from '../../redux/getFilteredActiveDeviceKeyMappingList'
import type { DeactivateKeyMappingState } from '../../redux/deactivateKeyMappingSlice'
import Modal from '../../../../components/Modal'

const KeyTypeMappingsStrings = LanguageStrings().HSMConfiguration.KeyTypeMappings

type Order = 'asc' | 'desc'

interface Sort {
  order: Order
  orderBy: string
}

const style = {
  width: '99%',
  bgcolor: '#313030',
  border: '1px solid #101010',
  borderRadius: '10px',
  boxShadow: '1px 1px 1px rgba(0, 0, 0, 0.15)',
  marginBottom: '16px',
  padding: 0
}

interface EnhancedTableProps {
  numSelected: number
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void
  rowCount: number
  dataList: HsmKeyTypeMapping[]
  order: Order
  orderBy: string
  onRequestSort: (event: React.MouseEvent<unknown>, newOrderBy: keyof HsmKeyTypeMapping) => void
}

function EnhancedTableHead (props: EnhancedTableProps): any {
  const { onSelectAllClick, order, orderBy, onRequestSort, numSelected, rowCount, dataList } = props
  const createSortHandler =
        (newOrderBy: keyof HsmKeyTypeMapping) => (event: React.MouseEvent<unknown>) => {
          onRequestSort(event, newOrderBy)
        }
  return (dataList !== null && dataList !== undefined && <>
        <TableHead data-testid='table-header'>
          <TableRow>
            <StyledTableCell padding="checkbox">
              <Checkbox
                sx={{ color: '#909090' }}
                data-testid="device-table-header-checkbox"
                indeterminate={numSelected > 0 && numSelected < rowCount}
                checked={rowCount > 0 && numSelected === rowCount}
                onChange={onSelectAllClick}
                inputProps={{
                  'aria-label': 'Select all Assets',
                  id: 'select-all-items'
                }}
              />
            </StyledTableCell>
              {headCells.map((headCell: HeadCell) => (
                  <StyledTableCell
                      key={headCell.id}
                      align='left'
                      padding={headCell.disablePadding ? 'none' : 'normal'}
                      sortDirection={orderBy === headCell.id ? order : false}
                  >
                      <TableSortLabel
                          hideSortIcon={!headCell.sortable}
                          direction={orderBy === headCell.id ? order : 'asc'}
                          onClick={headCell.sortable ? createSortHandler(headCell.id) : undefined}
                          aria-label={headCell.label}
                          data-testid="test-sort"
                          sx={{ cursor: headCell.sortable ? 'pointer' : 'text' }}
                      >
                          {headCell.id}
                          {orderBy === headCell.id
                            ? (
                                  // istanbul ignore next
                                  <Box component="span" sx={visuallyHidden}>
                                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                  </Box>
                              )
                            : null}
                      </TableSortLabel>
                  </StyledTableCell>
              ))}
          </TableRow>
        </TableHead>
      </>
  )
}

interface HeadCell {
  disablePadding: boolean
  id: any
  label: string
  numeric: boolean
  sortable: boolean
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: '#F0F0F0',
    fontWeight: '700',
    fontSize: 12,
    border: '2px solid #202020',
    padding: '5px 16px',
    fontFamily: 'Honeywell Sans Web',
    lineHeight: '20px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: '#d0d0d0',
    paddingLeft: '17px',
    borderBottom: '1px solid #202020',
    borderRight: 0,
    fontFamily: 'Honeywell Sans Web',
    whiteSpace: 'nowrap'
  }
}))

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '& th': {
    border: '1px solid #202020'
  }
}))

const headCells: readonly HeadCell[] = [
  {
    id: 'key type',
    numeric: false,
    disablePadding: false,
    label: KeyTypeMappingsStrings.KeyType,
    sortable: true
  },
  {
    id: 'tenant',
    numeric: false,
    disablePadding: false,
    label: KeyTypeMappingsStrings.Tenant,
    sortable: true
  },
  {
    id: 'label of hybrid kek',
    numeric: true,
    disablePadding: false,
    label: KeyTypeMappingsStrings.LabelOfHybridKey,
    sortable: true
  },
  {
    id: 'created By',
    numeric: false,
    disablePadding: false,
    label: KeyTypeMappingsStrings.CreatedBy,
    sortable: true
  },
  {
    id: 'created At',
    numeric: false,
    disablePadding: false,
    label: KeyTypeMappingsStrings.CreatedAt,
    sortable: true
  },
  {
    id: 'action',
    numeric: false,
    disablePadding: false,
    label: KeyTypeMappingsStrings.Action,
    sortable: false
  }
]

interface FilterState {
  type: Option | null
}

const ActiveKeyMappings = (props: PropsFromRedux): JSX.Element => {
  const [activeKeyMappingsList, setActiveKeyMappingsList] = React.useState<HsmKeyTypeMapping[]>([])
  const [selected, setSelected] = React.useState<string[]>([])
  const [checkedKek, setCheckedRegularKekList] = React.useState<HsmKeyTypeMapping[]>([])
  const [page, setPage] = React.useState(1)
  const [sort, setSort] = React.useState<Sort>({ order: 'desc', orderBy: 'created At' })
  const [searchValue, setSearchValue] = React.useState<string | null>('')
  const [uiLoading, setUiLoading] = React.useState(false)
  const rowsPerPage = 10

  const [openFilter, setOpenFilter] = React.useState(false)
  const [showfilter, setShowFilter] = React.useState(false)
  const [typeOptions, setTypeOptions] = React.useState<Option[]>([])
  const [filterState, setFilterState] = React.useState<FilterState>({ type: null })
  const [appliedFilters, setAppliedFilters] = React.useState<FilterState>({ type: null })
  const MapppingFilterParameter = useSelector((state: RootState) => state.filterTypesForDeviceKeyMappings.data)
  const deactivateState: DeactivateKeyMappingState = useSelector((state: RootState) => state.deactivateKeyMapping)
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const pendingDeactivateRef = React.useRef<KeyTypeMappingRequest | null>(null)
  const [successOpen, setSuccessOpen] = React.useState(false)
  const localDeactivateCycleRef = React.useRef(false)
  const [lastDeactivatedName, setLastDeactivatedName] = React.useState<string | null>(null)

  const calledOnce = useRef(false)
  const lastFetchKeyRef = useRef<string | null>(null)
  React.useEffect(() => {
    if (!calledOnce.current) {
      props.fetchFilterTypesForKeyDeviceMappings()
      calledOnce.current = true
    }
  }, [])

  React.useEffect(() => {
    setUiLoading(true)
  }, [])

  const handleTypeSelect = (selectedValue: Option): void => {
    setFilterState({ ...filterState, type: selectedValue })
  }

  const resetFilterState = (): void => {
    setFilterState(appliedFilters)
    setOpenFilter(false)
  }

  const resetAppliedFilters = (): void => {
    setAppliedFilters({ type: null })
  }

  const showFilterDetails = (pageNum: number): void => {
    props.fetchFilteredActiveDeviceKeyMappingList(
      pageNum,
      rowsPerPage,
      sort.order,
      sort.orderBy,
      searchValue ?? '',
      {
        keyTypeName: filterState.type === null ? [] : [filterState.type.value]
      }
    )
  }

  const showAppliedFilterDetails = (pageNum: number): void => {
    props.fetchFilteredActiveDeviceKeyMappingList(
      pageNum,
      rowsPerPage,
      sort.order,
      sort.orderBy,
      searchValue ?? '',
      {
        keyTypeName: appliedFilters.type === null ? [] : [appliedFilters.type.value]
      }
    )
  }

  const filtersUpdated = (): boolean => {
    return (filterState.type !== appliedFilters.type)
  }

  const handleRowClick = (id: string): void => {
    const isIdSelected = selected.includes(id)
    if (!isIdSelected) {
      setCheckedRegularKekList([...checkedKek, activeKeyMappingsList.filter((row: HsmKeyTypeMapping) => row.id === id)[0]])
      setSelected([...selected, id])
    } else {
      const temp = [...checkedKek].filter((row: HsmKeyTypeMapping) => row.id !== id)
      setCheckedRegularKekList(temp)
      setSelected(selected.filter((selectedId) => selectedId !== id))
    }
  }

  const handleFetchMappingsList = (): void => {
    if (page >= 1) {
      props.fetchKeyTypeMappingsList(
        page - 1, rowsPerPage, sort.order, sort.orderBy, searchValue ?? ''
      )
    }
  }

  React.useEffect(() => {
    const currentPageIdx = Math.max(page - 1, 0)
    const key = JSON.stringify({
      filt: appliedFilters.type?.value ?? null,
      page: currentPageIdx,
      size: rowsPerPage,
      order: sort.order,
      orderBy: sort.orderBy,
      q: searchValue ?? ''
    })
    if (lastFetchKeyRef.current === key) return
    if (appliedFilters.type === null) {
      setUiLoading(true)
      props.fetchKeyTypeMappingsList(currentPageIdx, rowsPerPage, sort.order, sort.orderBy, searchValue ?? '')
      lastFetchKeyRef.current = key
    } else {
      setUiLoading(true)
      showFilterDetails(currentPageIdx)
      lastFetchKeyRef.current = key
    }
  }, [appliedFilters, page, sort, searchValue])

  React.useEffect(() => {
    const isAnyLoading = appliedFilters.type !== null ? props.hsmKeyTypeMappingList.isLoading : props.keyTypeMappingsList.isLoading
    if (!isAnyLoading) {
      setUiLoading(false)
    }
  }, [props.keyTypeMappingsList.isLoading, props.hsmKeyTypeMappingList.isLoading, appliedFilters])

  const handleSearch = (): void => {
    if (page >= 1) {
      setPage(1)
      setFilterState({ type: null })
      setAppliedFilters({ type: null })
      props.clearFilteredKeyMappings()
    }
  }

  React.useEffect(() => {
    const arr = MapppingFilterParameter ?? []
    setTypeOptions(arr.map(item => ({ value: item, label: item })))
  }, [MapppingFilterParameter])

  React.useEffect(() => {
    if (deactivateState.httpStatus === 200 && !deactivateState.isLoading && localDeactivateCycleRef.current) {
      setSelected([])
      setCheckedRegularKekList([])
      const currentPageIdx = Math.max(page - 1, 0)
      if (appliedFilters.type === null) {
        props.fetchKeyTypeMappingsList(
          currentPageIdx, rowsPerPage, sort.order, sort.orderBy, searchValue ?? ''
        )
      } else {
        showAppliedFilterDetails(currentPageIdx)
      }
      setSuccessOpen(true)
      localDeactivateCycleRef.current = false
    }
  }, [deactivateState.httpStatus, deactivateState.isLoading])

  React.useEffect(() => {
    setSuccessOpen(false)
    localDeactivateCycleRef.current = false
  }, [])

  const handleRequestSort =
        (event: React.MouseEvent<unknown>, newOrderBy: keyof HsmKeyTypeMapping): void => {
          const isAsc = sort.orderBy === newOrderBy && sort.order === 'asc'
          const toggledOrder = isAsc ? 'desc' : 'asc'
          setSort({ order: toggledOrder, orderBy: newOrderBy })
          lastFetchKeyRef.current = null
          setSelected([])
          setCheckedRegularKekList([])
          setUiLoading(true)
        }

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const currentPageKekList = activeKeyMappingsList.map((regularKEK: HsmKeyTypeMapping) => regularKEK.id)
    if (event.target.checked) {
      const newSelected: string[] = []
      const newSelectedKeks: HsmKeyTypeMapping[] = []
      currentPageKekList.forEach((id) => {
        if (!selected.includes(id)) {
          newSelected.push(id)
        }
      })
      activeKeyMappingsList.forEach((row: HsmKeyTypeMapping) => {
        if (newSelected.includes(row.id)) {
          newSelectedKeks.push(row)
        }
      })
      setSelected([...selected, ...newSelected])
      setCheckedRegularKekList([...checkedKek, ...newSelectedKeks])
    } else {
      const newSelected: string[] = []
      selected.forEach((id) => {
        if (!currentPageKekList.includes(id)) {
          newSelected.push(id)
        }
      })
      const newSelectedKeks: HsmKeyTypeMapping[] = [...checkedKek].filter((row: HsmKeyTypeMapping) => !currentPageKekList.includes(row.id))
      setSelected(newSelected)
      setCheckedRegularKekList(newSelectedKeks)
    }
  }

  const isSelected = (id: string): boolean => selected.includes(id)

  const getDisplayKekList = (): { list: HsmKeyTypeMapping[], total_count: number } => {
    if (appliedFilters.type !== null && props.hsmKeyTypeMappingList.data !== null) {
      return {
        list: props.hsmKeyTypeMappingList.data.list ?? [],
        total_count: props.hsmKeyTypeMappingList.data.total_count ?? 0
      }
    }
    return {
      list: props.keyTypeMappingsList.data?.list ?? [],
      total_count: props.keyTypeMappingsList.data?.total_count ?? 0
    }
  }

  useEffect(() => {
    setActiveKeyMappingsList(getDisplayKekList().list)
  }, [props.keyTypeMappingsList, props.hsmKeyTypeMappingList, appliedFilters])

  const renderTable = (kekList: any[], totalCount: number): JSX.Element => (
        <>
            <Paper sx={{
              width: '100%',
              mb: 2,
              backgroundColor: '#272727',
              boxShadow: 'unset',
              color: '#F0F0F0',
              marginBottom: '0'
            }}>
                <TableContainer className="device-table">
                    <Table sx={{ minWidth: 750, color: '#f0f0f0' }} aria-labelledby="tableTitle" size={'small'}>
                        <EnhancedTableHead
                            numSelected={selected.filter((label) => kekList.map((kek: HsmKeyTypeMapping) => kek.id).includes(label)).length}
                            order={sort.order}
                            orderBy={sort.orderBy}
                            onRequestSort={handleRequestSort}
                            onSelectAllClick={handleSelectAllClick}
                            rowCount={kekList.length}
                            dataList={kekList}
                        />
                        <TableBody>
                          {kekList.map((row: HsmKeyTypeMapping, index: number) => {
                            const isItemSelected = isSelected(row.id)
                            const labelId = `enhanced-table-checkbox-${row.id}`
                            return (
                                    <StyledTableRow
                                        hover
                                        role="checkbox"
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        aria-label="groups-list-row"
                                        selected={isItemSelected}
                                    >
                                        <StyledTableCell padding="checkbox">
                                            <Checkbox
                                                data-testid={`checkbox-${index}`}
                                                sx={{ color: '#909090' }}
                                                checked={isItemSelected}
                                                onChange={() => {
                                                  handleRowClick(row.id)
                                                }}
                                                inputProps={{
                                                  'aria-labelledby': labelId,
                                                  'aria-label': 'test-checkbox',
                                                  id: `select-item-${index}`
                                                }}
                                            />
                                        </StyledTableCell>
                                        <StyledTableCell
                                            component="th"
                                            id={labelId}
                                            scope="row"
                                            padding="none"
                                            aria-label="group-name"
                                            data-testid="group-name"
                                            sx={{
                                              whiteSpace: 'nowrap',
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              maxWidth: '15vw',
                                              cursor: 'pointer'
                                            }}
                                        >
                                            {(row.keyTypeName != null) && row.keyTypeName.length > 12
                                              ? <abbr style={{ textDecoration: 'none' }}
                                                        title={row.keyTypeName}>{row.keyTypeName}</abbr>
                                              : row.keyTypeName}
                                        </StyledTableCell>
                                        <StyledTableCell>{row.tenant}</StyledTableCell>
                                        <StyledTableCell>{row.hybridKek}</StyledTableCell>
                                        <StyledTableCell
                                            sx={{
                                              overflow: 'hidden',
                                              textOverflow: 'ellipsis',
                                              maxWidth: '15vw',
                                              whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {(row.createdBy != null) && row.createdBy.length > 12
                                              ? <abbr style={{ textDecoration: 'none' }}
                                                        title={row.createdBy}>{row.createdBy}</abbr>
                                              : row.createdBy}
                                        </StyledTableCell>
                                        <StyledTableCell>{(row.createdAt != null) ? new Date(row.createdAt).toLocaleString() : ''}</StyledTableCell>
                                        <StyledTableCell>
                                            {row.deactivatedAt === null && (
                                                <span
                                                    data-testid={`delete-${index}`}
                                                    onClick={() => {
                                                      pendingDeactivateRef.current = { tenant: row.tenant, keyTypeName: row.keyTypeName }
                                                      setConfirmOpen(true)
                                                    }}
                                                    style={{
                                                      color: '#64c3ff',
                                                      textDecoration: 'none',
                                                      cursor: 'pointer'
                                                    }}
                                                >
                                                  {KeyTypeMappingsStrings.Deactivate}
                                                </span>
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                            )
                          })}
                        </TableBody>
                    </Table>
                </TableContainer>
                {totalCount > rowsPerPage && (
                    <Pagination
                        count={totalCount}
                        page={page}
                        setPage={setPage}
                        rowsPerPage={rowsPerPage}
                        siblingCount={1}
                        boundaryCount={1}
                    />
                )}
            </Paper>
            {selected.length === 0
              ? /* istanbul ignore next */ null
              : (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#272727',
                      borderTop: '2px solid #202020'
                    }}>
                        <GenericButton type='borderless' dataTestId='cancel-btn' onClick={() => {
                          setSelected([])
                        }}>
                            {KeyTypeMappingsStrings.Cancel}
                        </GenericButton>
                    </div>
                )}
        </>
  )

  const getDisplayHttpStatus = (): number | null => {
    return props.keyTypeMappingsList.httpStatus
  }

  const handleTable = (): JSX.Element => {
    const displayKekList = getDisplayKekList()
    const isFiltered = appliedFilters.type !== null
    const displayHttpStatus = isFiltered ? props.hsmKeyTypeMappingList.httpStatus : getDisplayHttpStatus()
    const isLoading = isFiltered ? props.hsmKeyTypeMappingList.isLoading : props.keyTypeMappingsList.isLoading

    if (isLoading || uiLoading) {
      return <div className='CircularprogressAmi'><CircularProgress/></div>
    }

    if ((displayKekList.list?.length ?? 0) > 0 && displayHttpStatus === 200) {
      return renderTable(displayKekList.list, displayKekList.total_count)
    }

    if (displayHttpStatus === 401) {
      return <div className='authError'><AuthError errorMessage="Unauthorized"/></div>
    }
    if (displayHttpStatus === 403) {
      return <div className='authError'><AuthError errorMessage="accessForbidden"/></div>
    }
    if (displayHttpStatus === 404) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }
    if (displayHttpStatus === 400) {
      return <div className="authError"><AuthError errorMessage="invalidSearchInput"/></div>
    }

    if (displayHttpStatus === 200 && ((displayKekList.list?.length ?? 0) === 0)) {
      return <div className='authError'><AuthError errorMessage="NoDataPresent"/></div>
    }

    if (displayHttpStatus === 500) {
      return <div className='authError'><AuthError errorMessage="cannotFetch" retry={() => { isFiltered ? showFilterDetails(page - 1) : handleFetchMappingsList() }}/></div>
    }
    return <div className='authError'><AuthError errorMessage="cannotFetch"/></div>
  }

  return (
        <>
            <Box sx={style} className="devices">
                <div className='searchSection' style={{ width: '100%', backgroundColor: '#272727' }}>
                    <Paper className='searchBoxComponent'
                           sx={{ p: '2px 4px', display: 'flex', alignItems: 'center' }}
                    >
                        <Button data-testid='filter-data' className="filter-btn"
                                sx={{ background: '#707070', color: '#D0D0D0' }} onClick={() => {
                                  setOpenFilter(true)
                                }}><img src={FilterIcon} height={16} width={16}
                                style={{ marginLeft: '7px' }}/>{KeyTypeMappingsStrings.Filter}</Button>
                        <SearchBox immediate={true} debounce={true}
                                   placeholder={KeyTypeMappingsStrings.SearchUsingTenantLabelNameAndCreatedBy}
                                   sx={{ width: '450px' }} emptyEnterFallback={() => {
                                     page >= 1 && props.fetchKeyTypeMappingsList(page - 1, rowsPerPage, sort.order, sort.orderBy, '')
                                   }} searchVal={searchValue} setSearchVal={setSearchValue}
                                   performAction={() => {
                                     handleSearch()
                                   }}/>
                    </Paper>
                </div>
                <Drawer
                    anchor="right"
                    open={openFilter}
                    className='filter-drawer'
                    variant='temporary'
                    onClose={() => {
                      setOpenFilter(false)
                      resetFilterState()
                    }}
                >
                    <div className='filter-headar'>
                        <div className='title'
                             style={{ marginTop: '15px' }}>{KeyTypeMappingsStrings.Filter}</div>
                        <div><Button aria-label='clear-all-filter' className='cancel'
                                     style={{ marginTop: '-2px', marginLeft: '80px' }} onClick={() => {
                                       setFilterState({ type: null })
                                     }}><ReplayIcon sx={{
                                       width: '16px',
                                       height: '16px',
                                       marginRight: '8px'
                                     }}/>{KeyTypeMappingsStrings.ClearAll}</Button></div>
                        <div><CloseIcon aria-label='close-drawer' sx={{ color: '#f0f0f0', cursor: 'pointer' }}
                                        onClick={() => {
                                          setOpenFilter(false)
                                          resetFilterState()
                                        }}/>
                        </div>
                    </div>
                    <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                        <Grid item xs={12}>
                            <div className='type-name'
                                 style={{ marginTop: '40px' }}>{KeyTypeMappingsStrings.KeyType}</div>
                            <GenXDropdown placeholder={KeyTypeMappingsStrings.SelectKeyType}
                                          value={filterState.type} options={typeOptions} onSelect={handleTypeSelect}/>
                        </Grid>
                    </Grid>
                    <div className='divider'/>
                    <div className='buttons-container'>
                        <Button className='cancel' aria-label='cancel-drawer' onClick={() => {
                          resetFilterState()
                          setOpenFilter(false)
                        }}>{KeyTypeMappingsStrings.Cancel}</Button>
                        <GenericButton aria-label='confirm-filter' data-testid='show-results'
                                       primaryColor={!filtersUpdated() ? '#B0B0B0' : undefined}
                                       disabled={!filtersUpdated()}
                                       onClick={() => {
                                         setAppliedFilters(filterState)
                                         setShowFilter(true)
                                         setOpenFilter(false)
                                         setPage(1)
                                         showFilterDetails(0)
                                         setSearchValue(null)
                                       }}>{KeyTypeMappingsStrings.ShowDetails}</GenericButton>
                    </div>
                </Drawer>
                {showfilter &&
                  <div
                    style={{
                      padding: '12px 24px',
                      borderTop: '2px solid #202020',
                      color: '#d0d0d0'
                    }}
                  >
                      {appliedFilters.type === null
                        ? (
                            KeyTypeMappingsStrings.NoFiltersApplied
                          )
                        : (
                                  <div className="classNAme">
                                    {(props.keyTypeMappingsList.data?.total_count ?? getDisplayKekList().total_count)} {KeyTypeMappingsStrings.ResultsOrFilters}
                                  <>
                                      <div className="box">
                                      {' '}
                                      {KeyTypeMappingsStrings.KeyType} : {' '}
                                      {appliedFilters.type.label}
                                      </div>
                                  </>
                                  <em
                                      onClick={() => {
                                        resetAppliedFilters()
                                        setFilterState({ type: null })
                                        props.clearFilteredKeyMappings()
                                      }}
                                      style={{ cursor: 'pointer' }}
                                  >
                                      {KeyTypeMappingsStrings.ClearFilters}{' '}
                                  </em>
                              </div>
                          )}
                  </div>
                }
                {handleTable()}
                {confirmOpen && (
                  <Modal
                    buttonTypeNegative='hollow'
                    title={KeyTypeMappingsStrings.Deactivate}
                    LabelNegative={KeyTypeMappingsStrings.Cancel}
                    LabelPositive={KeyTypeMappingsStrings.Deactivate}
                    onClickPositive={() => {
                      setConfirmOpen(false)
                      if (pendingDeactivateRef.current != null) {
                        localDeactivateCycleRef.current = true
                        setLastDeactivatedName(pendingDeactivateRef.current.keyTypeName ?? null)
                        props.deactivateKeyMapping(pendingDeactivateRef.current)
                      }
                      pendingDeactivateRef.current = null
                    }}
                    onClose={() => { setConfirmOpen(false); pendingDeactivateRef.current = null }}
                    onClickNegative={() => { setConfirmOpen(false); pendingDeactivateRef.current = null }}
                  >
                    <p style={{ paddingTop: '1.2em' }}>
                      {KeyTypeMappingsStrings.DoYouWantToDeactivateThisKeyTypeMapping} {pendingDeactivateRef.current?.keyTypeName ?? ''}?
                    </p>
                  </Modal>
                )}
                {successOpen && (
                  <Modal
                    buttonTypeNegative='hollow'
                    title={KeyTypeMappingsStrings.Deactivate}
                    LabelNegative={KeyTypeMappingsStrings.Cancel}
                    LabelPositive={KeyTypeMappingsStrings.OK}
                    onClickPositive={() => { setSuccessOpen(false) }}
                    onClose={() => { setSuccessOpen(false) }}
                    onClickNegative={() => { setSuccessOpen(false) }}
                  >
                    <p style={{ paddingTop: '1.2em' }}>
                      {`${String(lastDeactivatedName ?? KeyTypeMappingsStrings.TheKeyTypeMapping)} ${String(KeyTypeMappingsStrings.HasBeenDeactivatedSuccessfully)}`}
                    </p>
                  </Modal>
                )}
            </Box>
        </>
  )
}

interface DispatchToProps {
  fetchKeyTypeMappingsList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => void
  fetchFilteredActiveDeviceKeyMappingList: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => void
  fetchFilterTypesForKeyDeviceMappings: () => void
  deactivateKeyMapping: (mapping: KeyTypeMappingRequest) => void
  clearFilteredKeyMappings: () => void
}

const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchFilterTypesForKeyDeviceMappings: () => dispatch(fetchFilterTypesForKeyDeviceMappings()),
  fetchFilteredActiveDeviceKeyMappingList: (page: number, size: number, sortdir: string, sortfield: string, input: string, filterParams: any) => dispatch(fetchFilteredActiveDeviceKeyMappingList(page, size, sortdir, sortfield, input, filterParams)),
  fetchKeyTypeMappingsList: (page: number, size: number, sortdir: string, sortfield: string, input: string) => dispatch(fetchKeyTypeMappingsList(page, size, sortdir, sortfield, input)),
  deactivateKeyMapping: (mapping: KeyTypeMappingRequest) => dispatch(deactivateKeyMapping(mapping)),
  clearFilteredKeyMappings: () => dispatch(hsmKeyTypeMappingListLoadingAction())
})

interface StateToProps {
  keyTypeMappingsList: KeyMappingsListState
  hsmKeyTypeMappingList: HsmKeyTypeMappingListState
}

const mapStateToProps = (state: RootState): StateToProps => ({
  keyTypeMappingsList: state.keyTypeMappingsList,
  hsmKeyTypeMappingList: state.filterActiveKeyTypeMappingsList
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>
export default connector(ActiveKeyMappings)
